import type { ReactNode } from "react";
import { cn } from "../lib/cn";
import { CARD_EYEBROW } from "./type";

export interface EyebrowBulletProps {
  children: ReactNode;
  /**
   * `card` - the patient `/today` editorial eyebrow used by Outcome /
   *           Plan / Environment / Summary / ThriveScore (sans, semi-
   *           bold, 10.5px, tracking 0.16em, teal-700).
   * `crumb` - the page-level breadcrumb at the top of the greeting
   *           (mono, 10.5px, same teal). Used once per page.
   * `plain` - the `/today` card eyebrow with no bullet (mono, text-2xs,
   *           tracking 0.12em), the same setting the Heartbeat sense card
   *           gives "Keeping an eye on", so the rail cards and the attention
   *           column share one eyebrow. A card whose title is the hero does
   *           not need a mark above it competing for the eye.
   */
  variant?: "card" | "crumb" | "plain";
  className?: string;
}

/**
 * EyebrowBullet, the canonical "● TEAL UPPERCASE LABEL" pattern that
 * tops every editorial card on the patient surface. Centralising it
 * here keeps the dot size, spacing, weight, and tracking in lockstep
 * across surfaces, drift between five copies of this string is the
 * usual cause of "this card looks slightly off" bug reports.
 */
export function EyebrowBullet({ children, variant = "card", className }: EyebrowBulletProps) {
  const plain = variant === "plain";
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 whitespace-nowrap uppercase text-teal-700",
        plain
          ? CARD_EYEBROW
          : cn(
              "text-[10.5px] tracking-[0.16em]",
              variant === "card" ? "font-sans font-semibold" : "font-mono",
            ),
        className,
      )}
    >
      {plain ? null : (
        <span aria-hidden="true" className="h-[5px] w-[5px] rounded-full bg-teal-600" />
      )}
      {children}
    </span>
  );
}
